'use client';

import React, { useState } from 'react';
import { toast } from 'react-toastify';

import Button from '@/components/V2/Button';
import Spinner from '@/components/V2/Spinner';
import TextInput from '@/components/V2/TextInput';
import useForm from '@/helpers/hooks/useForm';
import useWebAlias from '@/helpers/hooks/useWebalias';
import { useCreateCustomerInterstitialMutation } from '@/store/api/interstitialPopupApi';

import { CART_ABANDONMENT, HOME_PAGE_COLLECTION, useInterstitialContext } from '../InterstitialModalsContext';
import { markDetailsSubmitted } from '../sessionStorageKeys';
import { FormData, FormErrors, formSchema, initialState, mapErrorFields, SHOWINTERSTITIALMODAL } from '../utils';

const InterstitialSignupForm = () => {
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const { data, openModal, closeModal, isCartPage } = useInterstitialContext();
  const [createCustomerIntersitial] = useCreateCustomerInterstitialMutation();
  const webaliasData = useWebAlias();

  const {
    data: formData,
    errors,
    handleChange,
    onSubmit: handleFormSubmit,
    setFormErrors,
  } = useForm<FormData, FormErrors>({
    initialState,
    errors: {} as FormErrors,
    validationSchema: formSchema as any,
  });

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const onValidSubmit = async () => {
    setIsLoadingModal(true);

    try {
      await createCustomerIntersitial({
        ...formData,
        interstitialPopupType: isCartPage ? CART_ABANDONMENT : HOME_PAGE_COLLECTION,
        webAlias: webaliasData.webalias ?? 'pawtree',
      })
        .unwrap()
        .then(res => {
          if (res.success) {
            // Mark that user has submitted details - prevents both popups from showing again
            markDetailsSubmitted();

            // Track successful form submission for GA4
            if (globalThis?.window?.dataLayer) {
              globalThis.window.dataLayer.push({
                event: 'popup_form_submit',
                popup_type: isCartPage ? 'cart_abandonment' : 'homepage_collection',
                webalias: webaliasData.webalias ?? 'pawtree',
                submission_result: res.message === 'Thanks for signing up !' ? 'new_signup' : 'returning_user',
              });
            }

            closeModal(SHOWINTERSTITIALMODAL);
            if (res.message === 'Thanks for signing up !') {
              openModal('showSuccessModal');
            }
            if (res.message === 'Great to see you again!') {
              openModal('showWelcomeBackModal');
            }
          } else {
            toast.error(res.message);
          }
        });
    } catch (error: any) {
      const errorsFields = mapErrorFields(error.data);
      setFormErrors(errorsFields);
      console.error(error);
    } finally {
      setIsLoadingModal(false);
    }
  };

  return (
    <form onSubmit={e => handleFormSubmit(e, onValidSubmit)}>
      <div className="v2-flex v2-flex-col v2-gap-2 v2-mb-2">
        <TextInput
          type="text"
          placeholder="First Name"
          inputClassName="!v2-text-[14px] !v2-pl-[10px] !v2-py-[10px] v2-leading-[14px] v2-rounded-xs"
          name="firstName"
          value={formData.firstName}
          error={errors.firstName}
          onChange={handleChange}
        />
        <TextInput
          type="text"
          placeholder="Last Name"
          inputClassName="!v2-text-[14px] !v2-pl-[10px] !v2-py-[10px] v2-leading-[14px] v2-rounded-xs"
          name="lastName"
          value={formData.lastName}
          error={errors.lastName}
          onChange={handleChange}
        />
        <TextInput
          type="email"
          placeholder="Email Address"
          inputClassName="!v2-text-[14px] !v2-pl-[10px] !v2-py-[10px] v2-leading-[14px] v2-rounded-xs"
          name="email"
          value={formData.email}
          error={errors.email}
          onChange={handleChange}
        />
      </div>
      <Button
        type="submit"
        btnSize="md"
        scheme="primary"
        variant="filled"
        className="v2-text-sm v2-leading-[28px] v2-font-sweet-sans-pro-medium v2-font-medium !v2-py-[9px] v2-flex v2-justify-center v2-items-center v2-gap-2 v2-w-full v2-mb-4"
        disabled={isLoadingModal}
      >
        {isLoadingModal && <Spinner />}
        {data?.informationPopup?.buttonText}
      </Button>
    </form>
  );
};

export default InterstitialSignupForm;
